import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getTechnicalQuestions, submitTechnicalAnswers } from '../../api';
import NetworkDiagram, { generateCustomLayeredNetzplan, fixNetzplanDependencies, calculateNetzplan } from './NetworkDiagram';
import GanttDiagram from './GanttDiagram';
import SqlQuiz from './SqlQuiz';
import './TechnicalQuiz.css';
import { useNavigate } from 'react-router-dom';

const DEMO_NETZPLAN_QUESTION = {
  id: 'netzplan-demo',
  question_text: 'Analysieren Sie das Netzplan-Diagramm für das Teambuilding-Event. Welcher ist der kritische Pfad? Wie viele Zeiteinheiten dauert das Projekt?',
  question_type: 'network_diagram',
  topicSlug: 'netzplan', // hinzugefügt
  data: {
    title: 'Teambuilding-Event Netzplan',
    description: 'Demo-Frage: Netzplan-UI ohne Backend-Anbindung'
  }
};

const TechnicalQuiz = ({ moduleSlug, moduleTitle, topicSlug, topicTitle }) => {
  const navigate = useNavigate();

  // Alle useState, useRef, useEffect, useCallback usw. HIER!
  const [questions, setQuestions] = useState([]);
  const originalQuestionsRef = useRef([]); // Speichert die Originalfragen
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [unansweredQuestions, setUnansweredQuestions] = useState([]);
  const [error, setError] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  // Refs für die Fragen-Karten
  const questionRefs = useRef({});
  // Netzplan-Tasks im State verwalten
  const [netzplanTasks, setNetzplanTasks] = useState(null);
  const [netzplanInputs, setNetzplanInputs] = useState({});
  const [netzplanErrors, setNetzplanErrors] = useState({});

  // Callback für Netzplan-Inputs
  const handleNetzplanInputChange = (vorgangId, field, value) => {
    setNetzplanInputs(prev => ({
      ...prev,
      [vorgangId]: {
        ...prev[vorgangId],
        [field]: value
      }
    }));
  };

  // Validierungsfunktion
  const validateNetzplanInputs = () => {
    if (!netzplanTasks) return {};
    const solution = calculateNetzplan(netzplanTasks);
    const errors = {};
    // Mappe User-Eingaben nach Vorgangsnummer (Großbuchstabe)
    for (const task of solution) {
      const user = netzplanInputs[task.id] || {};
      const taskErrors = {};
      if ((user.id || '').toUpperCase() !== task.id) taskErrors.id = true;
      if (String(user.dauer || '') !== String(task.dauer)) taskErrors.dauer = true;
      if (String(user.faz || '') !== String(task.faz)) taskErrors.faz = true;
      if (String(user.fez || '') !== String(task.fez)) taskErrors.fez = true;
      if (String(user.saz || '') !== String(task.saz)) taskErrors.saz = true;
      if (String(user.sez || '') !== String(task.sez)) taskErrors.sez = true;
      if (String(user.gp || '') !== String(task.gp)) taskErrors.gp = true;
      if (String(user.fp || '') !== String(task.fp)) taskErrors.fp = true;
      if (Object.keys(taskErrors).length > 0) taskErrors.module = true;
      errors[task.id] = taskErrors;
    }
    // --- Kritischer Pfad Prüfung ---
    // Finde die Frage mit Typ network_diagram
    const netzplanQuestion = questions.find(q => q.question_type === 'network_diagram');
    if (netzplanQuestion && netzplanTasks) {
      // Berechne den kritischen Pfad (als Array von Buchstaben)
      const solutionCriticalPath = getCriticalPath(netzplanTasks); // Hilfsfunktion unten
      const userPfadRaw = (answers[netzplanQuestion.id] || '');
      const userPfad = userPfadRaw.replace(/\s+/g, '').toUpperCase().split(',');
      const isPfadCorrect =
        userPfad.length === solutionCriticalPath.length &&
        userPfad.every((v, i) => v === solutionCriticalPath[i]);
      if (!isPfadCorrect) {
        errors['criticalPath'] = { message: 'Kritischer Pfad ist nicht korrekt.' };
      }
      // Gesamtdauer prüfen
      const userDauer = String(answers[`${netzplanQuestion.id}_duration`] || '').trim();
      const solutionDauer = String(Math.max(...netzplanTasks.map(t => t.fez)));
      if (userDauer !== solutionDauer) {
        errors['totalDuration'] = { message: 'Gesamtdauer ist nicht korrekt.' };
      }
    }
    return errors;
  };

  // Hilfsfunktion für den kritischen Pfad (gibt Array wie ['A','B','D','G'] zurück)
  function getCriticalPath(tasks) {
    // Finde alle Pfade von Start zu Ende, wähle den mit größter Dauer
    // Finde Startknoten (keine Vorgänger)
    const startNodes = tasks.filter(t => !t.dependsOn || t.dependsOn.length === 0);
    // Finde Endknoten (keine Nachfolger)
    const endNodes = tasks.filter(t => !tasks.some(x => (x.dependsOn || []).includes(t.id)));
    let longestPath = [];
    let maxDuration = 0;
    function dfs(node, path, duration) {
      path.push(node.id);
      duration += node.dauer;
      if (endNodes.some(e => e.id === node.id)) {
        if (duration > maxDuration) {
          maxDuration = duration;
          longestPath = [...path];
        }
      } else {
        const successors = tasks.filter(t => (t.dependsOn || []).includes(node.id));
        for (const succ of successors) {
          dfs(succ, path, duration);
        }
      }
      path.pop();
    }
    for (const start of startNodes) {
      dfs(start, [], 0);
    }
    return longestPath;
  }

  const loadQuestions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      let loadedQuestions = [];
      let loadedSessionId = null;
      // SQL-Topic: Backend-Request auf technische Fragen überspringen
      if (topicSlug === 'sql') {
        originalQuestionsRef.current = [];
        setQuestions([]);
        setSessionId(null);
        return;
      }
      if (moduleSlug === 'lernfeld-4') {
        // Demo-Frage immer als erste Frage einfügen
        loadedQuestions = [DEMO_NETZPLAN_QUESTION];
        // Aktuell keine Backend-Fragen für Lernfeld 4 laden, um 404 zu vermeiden
        originalQuestionsRef.current = loadedQuestions;
        setQuestions(loadedQuestions);
        setSessionId(null);
        return;
      }
      // Backend-Fragen laden
      try {
        const response = await getTechnicalQuestions(moduleSlug);
        if (response.data.questions && response.data.questions.length > 0) {
          loadedQuestions = loadedQuestions.concat(response.data.questions);
          loadedSessionId = response.data.sessionId;
        }
      } catch (err) {
        // Wenn Backend 404 liefert, ignoriere den Fehler für lernfeld-4
        if (!(moduleSlug === 'lernfeld-4' && err.response && err.response.status === 404)) {
          throw err;
        }
      }
      // Speichere alle geladenen Fragen in der Ref
      originalQuestionsRef.current = loadedQuestions;
      
      // Filtere Fragen nach topicSlug, falls vorhanden
      let questionsToSet = loadedQuestions;
      if (topicSlug && loadedQuestions.length > 0) {
        questionsToSet = loadedQuestions.filter(q => 
          (q.topicSlug || q.topic) === topicSlug || q.id === 'netzplan-demo'
        );
      }
      
      setQuestions(questionsToSet);
      setSessionId(loadedSessionId);
      if (loadedQuestions.length === 0) {
        setError('Keine technischen Fragen für dieses Modul gefunden.');
      }
    } catch (error) {
      setError('Fehler beim Laden der Fragen: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  }, [moduleSlug, topicSlug]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  useEffect(() => {
    if (moduleSlug === 'lernfeld-4') {
      // Netzplan generieren oder aus localStorage holen
      const key = 'netzplan_lernfeld4';
      let tasks = null;
      const stored = localStorage.getItem(key);
      if (stored) {
        try {
          tasks = JSON.parse(stored);
        } catch {}
      }
      if (!tasks) {
        tasks = generateCustomLayeredNetzplan(7);
        tasks = fixNetzplanDependencies(tasks);
      }
      setNetzplanTasks(tasks);
    }
  }, [moduleSlug]);

  const handleAnswerChange = (questionId, selectedAnswer, questionType) => {
    setAnswers(prev => {
      if (questionType === 'multiple_choice') {
        return { ...prev, [questionId]: selectedAnswer };
      } else if (questionType === 'fill_in') {
        return { ...prev, [questionId]: selectedAnswer };
      } else if (questionType === 'order') {
        const prevArr = Array.isArray(prev[questionId]) ? prev[questionId] : [];
        let newArr;
        if (prevArr.includes(selectedAnswer)) {
          newArr = prevArr.filter((a) => a !== selectedAnswer);
        } else {
          newArr = [...prevArr, selectedAnswer];
        }
        return { ...prev, [questionId]: newArr };
      } else if (questionType === 'network_diagram') {
        return { ...prev, [questionId]: selectedAnswer };
      }
      return prev;
    });
    setUnansweredQuestions((prev) => prev.filter((id) => id !== questionId));
  };

  const scrollToFirstUnanswered = () => {
    if (unansweredQuestions.length > 0) {
      const firstUnansweredId = unansweredQuestions[0];
      const element = questionRefs.current[firstUnansweredId];
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        setTimeout(() => {
          element.focus();
        }, 500);
      }
    }
  };

  const handleSubmit = async () => {
    setIsSubmitted(true);
    if (moduleSlug === 'lernfeld-4') {
      localStorage.removeItem('netzplan_lernfeld4');
    }
    // Netzplan-Validierung
    let unanswered = [];
    if (netzplanTasks) {
      const errors = validateNetzplanInputs();
      setNetzplanErrors(errors);
      const netzplanQuestion = questions.find(q => q.question_type === 'network_diagram');
      if (netzplanQuestion) {
        // Prüfe, ob beide Felder ausgefüllt sind
        const pfadInput = (answers[netzplanQuestion.id] || '').trim();
        const dauerInput = (answers[`${netzplanQuestion.id}_duration`] || '').toString().trim();
        if (!pfadInput) {
          unanswered.push(netzplanQuestion.id + '_criticalPath');
        }
        if (!dauerInput) {
          unanswered.push(netzplanQuestion.id + '_totalDuration');
        }
      }
      const hasErrors = Object.values(errors).some(e => Object.keys(e).length > 0);
      if (hasErrors) {
        // Fehler anzeigen, nicht abschicken
        return;
      }
    }
    // Normale Fragen prüfen
    unanswered = unanswered.concat(
      questions.filter(q => {
        if (q.question_type === 'order') {
          return !answers[q.id] || answers[q.id].length === 0;
        } else if (q.question_type === 'network_diagram') {
          // Bereits oben geprüft
          return false;
        } else {
          return !answers[q.id];
        }
      }).map(q => q.id)
    );
    if (unanswered.length > 0) {
      setUnansweredQuestions(unanswered);
      setTimeout(scrollToFirstUnanswered, 100);
      return;
    }

    try {
      setSubmitting(true);
      const answersArray = Object.entries(answers).map(([questionId, selectedAnswer]) => ({
        questionId: parseInt(questionId),
        selectedAnswer
      }));

      const response = await submitTechnicalAnswers({
        sessionId,
        answers: answersArray
      });

      setResults(response.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    } catch (error) {
      console.error('❌ Fehler bei technischer Auswertung:', error);
      setError('Fehler beim Auswerten: ' + (error.response?.data?.error || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestionContent = (question) => {
    switch (question.question_type) {
      case 'multiple_choice':
        return (
          <div className="options-container">
            {question.data?.options?.map((option, index) => (
              <label key={index} className="option-label">
                <input
                  type="radio"
                  name={`question-${question.id}`}
                  value={option}
                  checked={answers[question.id] === option}
                  onChange={() => handleAnswerChange(question.id, option, 'multiple_choice')}
                />
                <span className="option-text">{option}</span>
              </label>
            ))}
          </div>
        );
      
      case 'fill_in':
        return (
          <div className="fill-in-container">
            <input
              type="text"
              className="fill-in-input"
              placeholder="Ihre Antwort..."
              value={answers[question.id] || ''}
              onChange={(e) => handleAnswerChange(question.id, e.target.value, 'fill_in')}
            />
          </div>
        );
      
      case 'order':
        return (
          <div className="order-container">
            {question.data?.items?.map((item, index) => (
              <label key={index} className="order-option-label">
                <input
                  type="checkbox"
                  value={item}
                  checked={Array.isArray(answers[question.id]) && answers[question.id].includes(item)}
                  onChange={() => handleAnswerChange(question.id, item, 'order')}
                />
                <span className="order-option-text">{item}</span>
              </label>
            ))}
          </div>
        );
      
      case 'network_diagram':
        return (
          <div className="network-diagram-question">
            <NetworkDiagram 
              tasks={netzplanTasks} 
              onInputChange={handleNetzplanInputChange}
              errors={netzplanErrors}
              inputValues={netzplanInputs}
            />
            <div className="network-question-inputs-row">
              <div className="network-question-group" style={{ flex: 1, marginRight: '16px' }}>
                <label>Kritischer Pfad:</label>
                <input
                  type="text"
                  className={`network-input${netzplanErrors.criticalPath ? ' network-input-error' : ''}`}
                  placeholder="z.B. A, B, D, G"
                  value={answers[question.id] || ''}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value, 'network_diagram')}
                />
                {netzplanErrors.criticalPath && (
                  <div className="network-input-solution">
                    Richtige Lösung: {getCriticalPath(netzplanTasks).join(', ')}
                  </div>
                )}
              </div>
              <div className="network-question-group" style={{ flex: 1 }}>
                <label>Gesamtdauer:</label>
                <input
                  type="number"
                  className={`network-input${netzplanErrors.totalDuration ? ' network-input-error' : ''}`}
                  placeholder="Zeiteinheiten"
                  value={answers[`${question.id}_duration`] || ''}
                  onChange={(e) => handleAnswerChange(`${question.id}_duration`, e.target.value, 'network_diagram')}
                />
                {netzplanErrors.totalDuration && (
                  <div className="network-input-solution">
                    Richtige Lösung: {Math.max(...netzplanTasks.map(t => t.fez))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      
      default:
        return <p>Unbekannter Fragetyp: {question.question_type}</p>;
    }
  };

  // --- GANTT-DIAGRAMM: Nur leeren Container anzeigen, keine Netzplan-Logik laden ---
  if (topicSlug === 'gantt-diagram') {
    return <GanttDiagram />;
  }

  // --- SQL-QUIZ: Weiterleitung an SQL-Komponente ---
  if (topicSlug === 'sql') {
    return <SqlQuiz moduleSlug={moduleSlug} moduleTitle={moduleTitle} topicSlug={topicSlug} topicTitle={topicTitle} />;
  }

  if (loading) {
    return <div className="technical-quiz-loading">Lade technische Fragen...</div>;
  }

  if (error) {
    return (
      <div className="technical-quiz-error">
        <h3>Fehler</h3>
        <p>{error}</p>
        <button onClick={loadQuestions} className="retry-button">
          Erneut versuchen
        </button>
      </div>
    );
  }

  if (results) {
    // Debug-Ausgaben
    console.log('DEBUG results:', results);
    console.log('DEBUG originalQuestionsRef.current:', originalQuestionsRef.current);
    const isNetzplan = originalQuestionsRef.current.some(q => q.question_type === 'network_diagram');
    console.log('DEBUG isNetzplan:', isNetzplan);
    return (
      <div className="technical-quiz-results">
        {/* Debug-Ausgabe entfernt */}
        <h3>Technisches Quiz abgeschlossen!</h3>
        <div className="results-summary">
          <p>Richtige Antworten: {results.summary.correctAnswers} von {results.summary.totalQuestions}</p>
          <p>Ergebnis: {results.summary.scorePercentage}%</p>
          <div className={`score-badge ${results.summary.scorePercentage >= 70 ? 'passed' : 'failed'}`}>
            {results.summary.scorePercentage >= 70 ? 'Bestanden' : 'Nicht bestanden'}
          </div>
        </div>
        <div className="detailed-results">
          <h4>Detaillierte Ergebnisse:</h4>
          {results.results.map((result, index) => {
            const question = results.questionsData?.find(q => String(q.id) === String(result.questionId)) || 
                           questions.find(q => String(q.id) === String(result.questionId));
            
            return (
              <div key={result.questionId} className={`result-item ${result.isCorrect ? 'correct' : 'incorrect'}`}>
                <div className="result-content">
                  <div className="result-header">
                    <h5>Frage {index + 1}</h5>
                    <span className={`result-icon ${result.isCorrect ? 'correct' : 'incorrect'}`}>
                      {result.isCorrect ? '✓' : '✗'}
                    </span>
                  </div>
                  {question && (
                    <div className="result-details">
                      <p className="question-text">{question.question_text}</p>
                      <p className="user-answer">
                        <strong>Ihre Antwort:</strong> 
                        <span className={result.isCorrect ? 'correct-answer' : 'wrong-answer'}>
                          {Array.isArray(result.selectedAnswer) 
                            ? result.selectedAnswer.join(', ')
                            : result.selectedAnswer}
                        </span>
                      </p>
                      {!result.isCorrect && (
                        <p className="correct-answer-display">
                          <strong>Richtige Antwort:</strong> 
                          <span className="correct-answer">
                            {Array.isArray(result.correctAnswer) 
                              ? result.correctAnswer.join(', ')
                              : result.correctAnswer}
                          </span>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="quiz-footer" style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginTop: '32px' }}>
          <button 
            className="retry-button"
            onClick={() => navigate(`/learning/${moduleSlug}/topics`)}
          >
            Zurück
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`technical-quiz${moduleSlug === 'lernfeld-4' ? ' technical-quiz-lernfeld4' : ''}`}>
      {topicSlug !== 'netzplan' && (
        <div className="quiz-header">
          <h3>{moduleTitle} - Technisches Quiz</h3>
          <p>{questions.length} Fragen</p>
        </div>
      )}

      <div className="questions-container">
        {questions.map((question, index) => (
          <div 
            key={question.id}
            ref={el => questionRefs.current[question.id] = el}
            className={`question-card ${unansweredQuestions.includes(question.id) ? 'unanswered' : ''}`}
            tabIndex={-1}
          >
            <div className="question-header">
              {topicSlug !== 'netzplan' && (
                <h4>Frage {index + 1}</h4>
              )}
              {unansweredQuestions.includes(question.id) && (
                <span className="unanswered-warning">Bitte beantworten!</span>
              )}
            </div>
            
            <p className="question-text">{question.question_text}</p>
            
            {question.data && question.question_type === 'network_diagram' && netzplanTasks && (
              <div className="question-data">
                <table className="netzplan-table netzplan-table-wide">
                  <colgroup>
                    <col style={{ width: '16%' }} />
                    <col style={{ width: '34%' }} />
                    <col style={{ width: '16%' }} />
                    <col style={{ width: '34%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Vorgang</th>
                      <th>Beschreibung</th>
                      <th>Dauer</th>
                      <th>Vorgänger</th>
                    </tr>
                  </thead>
                  <tbody>
                    {netzplanTasks.map((task) => (
                      <tr key={task.id}>
                        <td>{task.id}</td>
                        <td>{task.name}</td>
                        <td>{task.dauer}</td>
                        <td>{task.dependsOn && task.dependsOn.length > 0 ? task.dependsOn.join(', ') : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {/* Für andere Fragetypen ggf. andere Datenanzeige lassen */}
            {question.data && question.question_type !== 'network_diagram' && (
              <div className="question-data">
                <pre className="data-display">{JSON.stringify(question.data, null, 2)}</pre>
              </div>
            )}
            
            {renderQuestionContent(question)}
          </div>
        ))}
      </div>

      <div className="quiz-footer" style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
        <button 
          onClick={handleSubmit} 
          disabled={submitting || isSubmitted}
          className="submit-button"
          style={{ display: isSubmitted ? 'none' : 'inline-block' }}
        >
          {submitting ? 'Wird ausgewertet...' : 'Quiz abschließen'}
        </button>
        <button 
          className="retry-button"
          onClick={() => navigate(`/learning/${moduleSlug}/topics`)}
          style={{ display: isSubmitted ? 'inline-block' : 'none' }}
        >
          Zurück
        </button>
      </div>
    </div>
  );
};

export default TechnicalQuiz; 